/**
 * Pure helpers for the markdown-table auto-styling pass.
 *
 * Two jobs, both deterministic from cell text:
 *   1. Classify a cell's textual sign — `'positive'`, `'negative'`, or
 *      `null` if it doesn't read as a signed number. The remark plugin
 *      uses this to add a `data-tone` attribute on the cell so CSS can
 *      colour the value.
 *   2. Detect "the column literally named Direction" so the remark
 *      plugin can auto-wrap the cell content in a `<Badge>`. Match is
 *      case-insensitive and ignores surrounding whitespace.
 *
 * Both helpers are deliberately kept here (separate from the remark
 * plugin) so they can be unit-tested under mocha+ts-node — same reason
 * the citation/badge marker logic is split. Pure functions, no AST
 * dependency.
 */
import type { Tone } from './components/tone';

/**
 * Classify a cell's textual content as signed positive, signed
 * negative, or null (no clear sign).
 *
 * Recognised positive forms:  `+12`, `+12.3%`, `+€4.2M`, `↑12%`, `+0.5`
 * Recognised negative forms:
 *   - leading `-`, `−` (Unicode minus), `–` (en dash) followed by a digit
 *   - parenthesised numerics: `(12)`, `(12.3%)`, `(€4.2M)`
 *   - leading `↓`
 *
 * Bare numbers (no sign), `0`, and "stable" descriptors return null and
 * are styled as the default neutral cell colour. This is intentional —
 * we don't want to colour every numeric cell, only ones whose direction
 * the model has explicitly signalled.
 */
export function classifyCellSign(rawText: string): Tone | null {
  const text = rawText.trim();
  if (text.length === 0) return null;

  // Positive markers
  if (text.startsWith('+') || text.startsWith('↑')) {
    return /\d/.test(text) ? 'positive' : null;
  }

  // Negative markers — `-`, Unicode minus `−`, en-dash `–`, leading `↓`
  if (text.startsWith('-') || text.startsWith('−') || text.startsWith('–')) {
    return /\d/.test(text) ? 'negative' : null;
  }
  if (text.startsWith('↓')) {
    return /\d/.test(text) ? 'negative' : null;
  }

  // Parenthesised negatives — `(12)`, `(€4.2M)`. Must contain a digit;
  // `(see ref.)` would otherwise false-positive.
  if (text.startsWith('(') && text.endsWith(')') && /\d/.test(text)) {
    return 'negative';
  }

  return null;
}

/**
 * Return true when the table column header reads literally as
 * "Direction" — case-insensitive, surrounding whitespace ignored. Used
 * by the remark plugin to opt-in cells in that column to the auto-badge
 * transform.
 */
export function isDirectionColumn(header: string): boolean {
  return header.trim().toLowerCase() === 'direction';
}

/**
 * Map the textual content of a Direction-column cell to a tone. Same
 * vocabulary as the badge marker but now content-driven rather than
 * caller-driven. Returns null when the cell text doesn't read as a
 * recognisable direction (in which case the plugin skips the badge and
 * leaves the cell as plain text).
 */
export function classifyDirectionCell(rawText: string): Tone | null {
  const text = rawText.trim().toLowerCase();
  if (text.length === 0) return null;

  // Sign-prefixed forms first — covers "+12%" / "-3pp" / "(2.5)".
  const signed = classifyCellSign(rawText);
  if (signed !== null) return signed;

  // Bare sign / arrow glyphs (PLT-5169). `classifyCellSign` requires a
  // digit after the sign, so a Direction cell carrying just the glyph
  // fell through and rendered as an unstyled plain-text cell. The
  // explicit Unicode minus `−` (U+2212) and en-dash `–` read as
  // "negative" here; the bare ASCII `-` and em-dash `—` deliberately
  // stay on the neutral path below — in tables they conventionally mean
  // "n/a / no data", not a downward direction.
  if (text === '+' || text === '↑') return 'positive';
  if (text === '−' || text === '–' || text === '↓') return 'negative';

  // Word-form direction labels.
  if (/^(up|rising|improving|better|positive|gain)/.test(text)) return 'positive';
  if (/^(down|falling|worsening|worse|negative|loss)/.test(text)) return 'negative';
  if (/^(flat|stable|unchanged|n\/a|—|-)$/.test(text)) return 'neutral';

  return null;
}
